import { useState, useCallback, useEffect } from 'react';
import api from '../../../services/api';

/**
 * Hook för att hantera mötesprotokoll
 *
 * @param {string} meetingId - ID för mötet
 */
export function useMeetingProtocol(meetingId) {
  const [protocol, setProtocol] = useState(null);
  const [canWrite, setCanWrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Hämta protokoll
  const fetchProtocol = useCallback(async () => {
    if (!meetingId) {
      setProtocol(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/meeting-protocols/meeting/${meetingId}`);
      setProtocol(response.data.data);
      setCanWrite(response.data.canWrite || false);
    } catch (err) {
      console.error('Fel vid hämtning av protokoll:', err);
      if (err.response?.status !== 404) {
        setError(err.response?.data?.message || 'Kunde inte hämta protokoll');
      }
      setProtocol(null);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  // Hämta vid mount och när meetingId ändras
  useEffect(() => {
    fetchProtocol();
  }, [fetchProtocol]);

  // Skapa protokoll
  const createProtocol = useCallback(async ({
    title,
    introduction,
    sections,
    conclusion,
    presentAttendees,
    includeNoteIds
  }) => {
    if (!meetingId) {
      throw new Error('Inget möte valt');
    }

    try {
      const response = await api.post('/meeting-protocols', {
        meetingId,
        title,
        introduction,
        sections,
        conclusion,
        presentAttendees,
        includeNoteIds
      });
      setProtocol(response.data.data);
      return response.data.data;
    } catch (err) {
      console.error('Fel vid skapande av protokoll:', err);
      throw err;
    }
  }, [meetingId]);

  // Uppdatera protokoll
  const updateProtocol = useCallback(async (protocolId, updates) => {
    try {
      const response = await api.put(`/meeting-protocols/${protocolId}`, updates);
      setProtocol(response.data.data);
      return response.data.data;
    } catch (err) {
      console.error('Fel vid uppdatering av protokoll:', err);
      throw err;
    }
  }, []);

  // Slutför protokoll
  const finalizeProtocol = useCallback(async (protocolId) => {
    try {
      const response = await api.post(`/meeting-protocols/${protocolId}/finalize`);
      setProtocol(response.data.data);
      return response.data.data;
    } catch (err) {
      console.error('Fel vid slutförande av protokoll:', err);
      throw err;
    }
  }, []);

  // Markera som skickat. `recipients` (valfri) = vald mottagarlista [{ userId, email, name, role }].
  // Utan recipients faller backend tillbaka till mötets deltagare (bakåtkompatibelt).
  const markAsSent = useCallback(async (protocolId, recipients = null) => {
    try {
      const body = Array.isArray(recipients) ? { recipients } : {};
      const response = await api.post(`/meeting-protocols/${protocolId}/send`, body);
      setProtocol(response.data.data);
      return response.data.data;
    } catch (err) {
      console.error('Fel vid markering som skickat:', err);
      throw err;
    }
  }, []);

  // Ta bort protokoll
  const deleteProtocol = useCallback(async (protocolId) => {
    try {
      await api.delete(`/meeting-protocols/${protocolId}`);
      setProtocol(null);
      return true;
    } catch (err) {
      console.error('Fel vid borttagning av protokoll:', err);
      throw err;
    }
  }, []);

  // Toggla actionpunkt
  const toggleActionPoint = useCallback(async (protocolId, sectionId, completed) => {
    try {
      const response = await api.patch(`/meeting-protocols/${protocolId}/sections/${sectionId}/toggle`, { completed });
      setProtocol(response.data.data);
      return response.data.data;
    } catch (err) {
      console.error('Fel vid toggle av actionpunkt:', err);
      throw err;
    }
  }, []);

  return {
    protocol,
    canWrite,
    loading,
    error,
    fetchProtocol,
    createProtocol,
    updateProtocol,
    finalizeProtocol,
    markAsSent,
    deleteProtocol,
    toggleActionPoint
  };
}

export default useMeetingProtocol;
